import * as storage from '@adapter/web-storage';

import { isEmpty } from '@groww-tech-pvt/ella';

import { WEBVIEW_PLATFORM_NAME, CONSTANTS } from './webviewConstants';

/**
 * Represents a semantic version with major, minor, patch and optional pre-release/build metadata.
 */
type SemverVersion = {
  major: number;
  minor: number;
  patch: number;
  preRelease?: string;
  buildMetadata?: string;
};

/**
 * Official REGEX from the Semver specification.
 * Matches semantic version strings in the format `MAJOR.MINOR.PATCH[-PRERELEASE][+BUILD_METADATA]`.
 */
const SEMVER_REGEX =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-((?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*)(?:\.(?:0|[1-9]\d*|\d*[a-zA-Z-][0-9a-zA-Z-]*))*))?(?:\+([0-9a-zA-Z-]+(?:\.[0-9a-zA-Z-]+)*))?$/;

/**
 * Checks if a string represents a valid number.
 * @param str - The string to validate.
 * @returns `true` if the string is a valid number, otherwise `false`.
 */
const isValidNumber = (str: string): boolean => {
  return /^\d+$/.test(str);
};

/**
 * Creates a new semantic version object.
 * @param major - The major version number (default: 0).
 * @param minor - The minor version number (default: 0).
 * @param patch - The patch version number (default: 0).
 * @param preRelease - Optional pre-release version identifier.
 * @param buildMetadata - Optional build metadata identifier.
 * @returns A SemverVersion object.
 */
const createSemver = (
  major = 0,
  minor = 0,
  patch = 0,
  preRelease?: string,
  buildMetadata?: string
): SemverVersion => ({
  major,
  minor,
  patch,
  preRelease,
  buildMetadata
});

/**
 * Parses a semantic version string into a SemverVersion object.
 * @param value - The semantic version string to parse.
 * @returns A SemverVersion object representing the parsed version.
 */
const parseSemver = (value: string): SemverVersion => {
  const results = value.match(SEMVER_REGEX);

  if (results) {
    const major = parseInt(results[1] ?? '0');
    const minor = parseInt(results[2] ?? '0');
    const patch = parseInt(results[3] ?? '0');

    return createSemver(major, minor, patch, results[4], results[5]);
  }

  // Try parsing using `.` splits as fallback
  const parts = value.split('.').map((val) => (val === '' ? '0' : val));
  const majorStr = parts[0] ?? '0';
  const minorStr = parts[1] ?? '0';
  const patchStr = parts[2] ?? '0';

  if (isValidNumber(majorStr) && isValidNumber(minorStr) && isValidNumber(patchStr)) {
    return createSemver(parseInt(majorStr), parseInt(minorStr), parseInt(patchStr));
  }

  return createSemver(0, 0, 0);
};

/**
 * Compares two SemverVersion objects based on major, minor, and patch versions.
 * @param v1 - The first SemverVersion object.
 * @param v2 - The second SemverVersion object.
 * @returns -1 if v1 < v2, 1 if v1 > v2, 0 if equal.
 */
const compareSemver = (v1: SemverVersion, v2: SemverVersion): number => {
  const versionTypes: (keyof Pick<SemverVersion, 'major' | 'minor' | 'patch'>)[] = [ 'major', 'minor', 'patch' ];

  for (const versionType of versionTypes) {
    if (v1[versionType] < v2[versionType]) {
      return -1;

    } else if (v1[versionType] > v2[versionType]) {
      return 1;
    }
  }

  return 0;
};

/**
 * Checks if the first version is less than the second version.
 * @param v1 - The first version to compare.
 * @param v2 - The second version to compare.
 * @returns `true` if v1 is less than v2, otherwise `false`.
 */
const isSemverLessThan = (v1: SemverVersion, v2: SemverVersion): boolean => {
  return compareSemver(v1, v2) === -1;
};

/**
 * Checks if the first version is greater than the second version.
 * @param v1 - The first version to compare.
 * @param v2 - The second version to compare.
 * @returns `true` if v1 is greater than v2, otherwise `false`.
 */
const isSemverGreaterThan = (v1: SemverVersion, v2: SemverVersion): boolean => {
  return compareSemver(v1, v2) === 1;
};

/**
 * Checks if the first version is equal to the second version.
 * @param v1 - The first version to compare.
 * @param v2 - The second version to compare.
 * @returns `true` if v1 is equal to v2, otherwise `false`.
 */
const isSemverEqualTo = (v1: SemverVersion, v2: SemverVersion): boolean => {
  return compareSemver(v1, v2) === 0;
};

export function getWebViewAppVersion() {
  const version = storage.getStorage(storage.TYPE.LOCAL_COOKIE_STORAGE, CONSTANTS.WEBVIEW_APP_VERSION, true);

  // Handle migration from legacy numeric versions to string versions
  if (typeof version === 'number') {
    // Convert legacy numeric version (e.g., 16.54) to string (e.g., "16.54.0")
    return `${version}.0`;
  }

  if (typeof version === 'string') {
    return version;
  }

  // Fallback for undefined/null/other types
  return '0.0.0';
}

export function getWebViewStatus() {
  return storage.getStorage(storage.TYPE.LOCAL_COOKIE_STORAGE, CONSTANTS.IS_WEBVIEW_ACTIVE, false);
}

export function getWebViewPlatform() {
  return (
    storage.getStorage(storage.TYPE.LOCAL_COOKIE_STORAGE, CONSTANTS.WEBVIEW_PLATFORM, false) ||
     storage.getStorage(storage.TYPE.LOCAL_COOKIE_STORAGE, CONSTANTS.WEBVIEW_PLATFORM_TYPE, false));
}

export function setWebViewAppVersion(webviewAppVersion : string) {
  storage.setStorage(storage.TYPE.LOCAL_COOKIE_STORAGE, CONSTANTS.WEBVIEW_APP_VERSION, webviewAppVersion, true);
}

/**
 * @returns This method will return the webview headers which will in turn be sent to the apis.
 * The method determines if we are in android or iOS and then prepares the headers accordingly. It also
 * sends the app version.
 */
export function getWebviewAppHeaders() {
  const webviewAppVersion = getWebViewAppVersion();
  const webviewPlatform = getWebViewPlatform();

  const ANDROID_HEADERS = {
    'x-platform': 'android',
    'X-APP-ID': 'growwAndroid'
  };
  const IOS_HEADERS = {
    'x-platform': 'ios',
    'X-APP-ID': 'growwIOS'
  };

  const platformHeaders = webviewPlatform === WEBVIEW_PLATFORM_NAME.IOS ? IOS_HEADERS : ANDROID_HEADERS;

  return {
    ...platformHeaders,
    ...(webviewAppVersion ? { 'x-platform-version': webviewAppVersion } : {})
  };
}


/**
 * @returns This method will return the webview meta data namely isWebview, webviewAppVersion
 * and webviewPlatform
 */
export function getWebviewMetaData() {
  const isWebView = getWebViewStatus();
  const webviewAppVersion = getWebViewAppVersion();
  const webviewPlatform = getWebViewPlatform();

  return {
    ...(isWebView ? { isWebView: 'MWeb' } : {}),
    ...(webviewAppVersion ? { webviewAppVersion } : {}),
    ...(webviewPlatform ? { webviewPlatform } : {})
  };
}


/**
 * This method checks if the user agent from window's navigator property, indicates the presence of a WebView.
 * @returns {boolean} true if the user agent indicates a WebView, false otherwise.
 */
export function getWebViewStatusFromUserAgent() : boolean {

  if (typeof window === 'undefined') {
    return false;
  }

  const userAgent = window?.navigator?.userAgent;

  if (isEmpty(userAgent)) {
    return false;
  }

  /**
  * A regular expression to check for the presence of a WebView in the user agent.
  * - For Android WebView, it checks for the presence of "Version" and "wv".
  * - For iOS WebView, it checks for the absence of "Safari".
  * @type {RegExp}
  */

  const regexForAndroidUserAgent = /Android.*?(?=.*\bVersion\b)(?=.*\bwv\b)/i;
  const regexForIOSUserAgent = /^(?=.*\b(iPad|iPhone)\b)(?!.*\bSafari\b)/i;

  const isAndroidWebView = regexForAndroidUserAgent.test(userAgent);
  const isIOSWebView = regexForIOSUserAgent.test(userAgent);

  const isWebView = isAndroidWebView || isIOSWebView;

  return isWebView;
}


/**
 * Retrieves the application name from the cookie storage.
 * Example: dashAndroid, dashIos
 *
 * @returns {string} The name of the application stored in the cookie.
 */

export const getWebViewAppName = () => {
  return storage.getStorage(storage.TYPE.COOKIE, CONSTANTS.WEBVIEW_APP_NAME, false);
};

/**
 * Compares two app version strings using semantic versioning.
 * @param version1 - The first version string to compare.
 * @param version2 - The second version string to compare.
 * @returns -1 if version1 < version2, 1 if version1 > version2, 0 if equal.
 */
export const compareAppVersions = (version1: string, version2: string): number => {
  const v1 = parseSemver(version1);
  const v2 = parseSemver(version2);

  return compareSemver(v1, v2);
};

/**
 * Checks if the first app version is less than the second app version.
 * @param version1 - The first version string to compare.
 * @param version2 - The second version string to compare.
 * @returns `true` if version1 is less than version2, otherwise `false`.
 */
export const isAppVersionLessThan = (version1: string, version2: string): boolean => {
  const v1 = parseSemver(version1);
  const v2 = parseSemver(version2);

  return isSemverLessThan(v1, v2);
};

/**
 * Checks if the first app version is greater than the second app version.
 * @param version1 - The first version string to compare.
 * @param version2 - The second version string to compare.
 * @returns `true` if version1 is greater than version2, otherwise `false`.
 */
export const isAppVersionGreaterThan = (version1: string, version2: string): boolean => {
  const v1 = parseSemver(version1);
  const v2 = parseSemver(version2);

  return isSemverGreaterThan(v1, v2);
};

/**
 * Checks if the first app version is equal to the second app version.
 * @param version1 - The first version string to compare.
 * @param version2 - The second version string to compare.
 * @returns `true` if version1 is equal to version2, otherwise `false`.
 */
export const isAppVersionEqualTo = (version1: string, version2: string): boolean => {
  const v1 = parseSemver(version1);
  const v2 = parseSemver(version2);

  return isSemverEqualTo(v1, v2);
};
